import { useState, useEffect } from 'react';
import { useCart } from '@/contexts/CartContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ArrowLeft, Minus, Plus, Trash2, AlertTriangle, Gift } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { getRestaurantStatus } from '@/utils/restaurantStatus';
import ComplementYourMeal from '@/components/ComplementYourMeal';
import { useBubblesBogo } from '@/hooks/useBubblesBogo';
import { BUBBLES_TROPICS_ID, calculateBubblesBogoDiscount, countBubblesBogoUnits } from '@/utils/bubblesBogo';


const CartPage = () => {
  const { items, removeItem, updateQuantity, total, itemCount, setCartType, addItem, getRestaurantId } = useCart();
  const navigate = useNavigate();
  const [restaurantStatuses, setRestaurantStatuses] = useState<{[key: string]: any}>({});
  const [bogoBasePrices, setBogoBasePrices] = useState<Record<string, number>>({});
  const {
    visible: bogoVisible,
    canRedeem: bogoCanRedeem,
    soldOut: bogoSoldOut,
    alreadyUsed: bogoAlreadyUsed,
  } = useBubblesBogo();
  const bogoUnits = countBubblesBogoUnits(items);
  const bogoSavings = bogoCanRedeem
    ? calculateBubblesBogoDiscount(items, bogoBasePrices)
    : 0;

  useEffect(() => {
    setCartType('restaurant');
    // Do NOT call refreshCart here — it can race with a pending DB write
    // and wipe items the user just added. Cart is already loaded on app init.
  }, [setCartType]);

  useEffect(() => {
    checkRestaurantStatuses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.length]);

  useEffect(() => {
    const loadBogoBasePrices = async () => {
      const menuItemIds = Array.from(new Set(
        items
          .filter((item) => item.restaurant_id === BUBBLES_TROPICS_ID)
          .map((item) => item.menu_item_id)
      ));

      if (!bogoVisible || menuItemIds.length === 0) {
        setBogoBasePrices({});
        return;
      }

      const { data, error } = await supabase
        .from('menu_items')
        .select('id, price')
        .in('id', menuItemIds);

      if (error) {
        console.error('Error loading Bubbles promotion prices:', error);
        return;
      }

      setBogoBasePrices(Object.fromEntries(
        (data || []).map((item) => [item.id, Number(item.price)])
      ));
    };

    loadBogoBasePrices();
  }, [items, bogoVisible]);


  const checkRestaurantStatuses = async () => {
    if (items.length === 0) return;
    
    // Get unique restaurant IDs from cart items
    const restaurantIds = [...new Set(items.map(item => item.restaurant_id))];
    
    try {
      const { data: restaurants, error } = await supabase
        .from('restaurants')
        .select('id, name, is_open, opening_hours')
        .in('id', restaurantIds);

      if (error) throw error;

      const statusMap: {[key: string]: any} = {};
      restaurants?.forEach(restaurant => {
        statusMap[restaurant.id] = {
          ...restaurant,
          statusInfo: getRestaurantStatus(restaurant)
        };
      });
      
      setRestaurantStatuses(statusMap);
    } catch (error) {
      console.error('Error checking restaurant statuses:', error);
    }
  };

  // Check if any restaurants in cart are closed
  const hasClosedRestaurants = Object.values(restaurantStatuses).some(
    (restaurant: any) => !restaurant.statusInfo?.canOrder
  );

  const getClosedRestaurants = () => {
    return Object.values(restaurantStatuses)
      .filter((restaurant: any) => !restaurant.statusInfo?.canOrder)
      .map((restaurant: any) => restaurant.name);
  };


  if (items.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="bg-white border-b border-gray-200 px-4 py-4">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(-1)}
            >
              <ArrowLeft className="h-6 w-6" />
            </Button>
            <h1 className="text-xl font-semibold">Your Cart</h1>
          </div>
        </div>
        
        <div className="flex flex-col items-center justify-center p-8 text-center">
          <div className="text-6xl mb-4">🛒</div>
          <h2 className="text-xl font-semibold mb-2">Your cart is empty</h2>
          <p className="text-gray-600 mb-6">Add items from restaurants to get started</p>
          <Button onClick={() => navigate('/home')}>
            Browse Restaurants
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-56">
      <div className="bg-white border-b border-gray-200 px-4 py-4">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="h-6 w-6" />
          </Button>
          <h1 className="text-xl font-semibold">Your Cart ({itemCount} items)</h1>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Show warning if any restaurants are closed */}
        {hasClosedRestaurants && (
          <Alert className="border-red-200 bg-red-50">
            <AlertTriangle className="h-4 w-4 text-red-600" />
            <AlertDescription className="text-red-800">
              Some restaurants in your cart are currently closed: {getClosedRestaurants().join(', ')}. 
              You cannot proceed with checkout until they reopen.
            </AlertDescription>
          </Alert>
        )}

        {bogoVisible && bogoUnits > 0 && (
          <div className="rounded-lg border border-pink-200 bg-pink-50 p-4">
            <div className="flex items-start gap-3">
              <div className="rounded-full bg-pink-500 p-2 text-white">
                <Gift className="h-5 w-5" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-bold text-pink-800">Buy 1 Get 1</p>
                <p className="mt-0.5 text-sm text-pink-700">
                  {bogoSoldOut
                    ? 'This offer has been fully claimed.'
                    : bogoAlreadyUsed
                      ? 'You have already used this offer.'
                      : bogoSavings > 0
                        ? `You’re saving D${bogoSavings.toFixed(2)} on this order!`
                        : 'Add one more Bubbles Tropics drink to get the cheaper drink free.'}
                </p>
              </div>
              {bogoSavings > 0 && (
                <span className="whitespace-nowrap font-bold text-green-600">
                  -D{bogoSavings.toFixed(2)}
                </span>
              )}
            </div>
          </div>
        )}

        {items.map((item) => {
          const restaurantStatus = restaurantStatuses[item.restaurant_id];
          const isRestaurantClosed = restaurantStatus && !restaurantStatus.statusInfo?.canOrder;
          
          return (
            <Card key={item.id} className={isRestaurantClosed ? 'opacity-60 border-red-200' : ''}>
              <CardContent className="p-4">
                <div className="flex items-center space-x-4">
                  {item.image_url && (
                    <img
                      src={item.image_url}
                      alt={item.name}
                      className="w-16 h-16 object-cover rounded-lg flex-shrink-0"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-sm truncate" title={item.name}>
                      {item.name}
                    </h3>
                    <div className="flex items-center gap-2">
                      <p className="text-xs text-gray-600 truncate" title={item.restaurant_name}>
                        {item.restaurant_name}
                      </p>
                      {isRestaurantClosed && (
                        <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded">
                          Closed
                        </span>
                      )}
                    </div>
                    <p className="text-lg font-semibold text-red-600">
                      D{(item.price * item.quantity).toFixed(2)}
                    </p>
                  </div>
                  <div className="flex items-center space-x-2 flex-shrink-0">
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => updateQuantity(item.id, item.quantity - 1)}
                    >
                      <Minus className="h-3 w-3" />
                    </Button>
                    <span className="text-sm font-semibold min-w-[1.5rem] text-center">
                      {item.quantity}
                    </span>
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => updateQuantity(item.id, item.quantity + 1)}
                    >
                      <Plus className="h-3 w-3" />
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-8 w-8 text-red-600 hover:text-red-700"
                      onClick={() => removeItem(item.id)}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}

        {/* Complement Your Meal Section */}
        {getRestaurantId() && (
          <ComplementYourMeal 
            currentRestaurantId={getRestaurantId() || ''} 
            onAddItem={(item, restaurantId, restaurantName, customizations, quantity, finalUnitPrice) => {
              addItem({
                menu_item_id: item.id,
                name: item.name,
                price: finalUnitPrice ?? item.price,
                image_url: item.image_url,
                restaurant_id: restaurantId,
                restaurant_name: restaurantName,
                cart_type: 'restaurant',
                is_complementary: true,
                customizations,
                quantity,
              });
            }}
          />

        )}
      </div>

      <div className="fixed bottom-20 left-0 right-0 bg-white border-t border-gray-200 p-4">
        {bogoSavings > 0 && (
          <div className="mb-2 flex items-center justify-between text-sm font-semibold text-green-600">
            <span>Promotion savings</span>
            <span>-D{bogoSavings.toFixed(2)}</span>
          </div>
        )}
        <div className="flex justify-between items-center mb-4">
          <span className="text-lg font-semibold">Estimated subtotal:</span>
          <span className="text-xl font-bold text-red-600">D{Math.max(total - bogoSavings, 0).toFixed(2)}</span>
        </div>
        <Button 
          className={`w-full ${hasClosedRestaurants ? 'bg-gray-400 hover:bg-gray-400 cursor-not-allowed' : 'bg-red-600 hover:bg-red-700'}`}
          onClick={() => !hasClosedRestaurants && navigate('/checkout')}
          disabled={hasClosedRestaurants}
        >
          {hasClosedRestaurants ? 'Some Restaurants Are Closed' : 'Proceed to Checkout'}
        </Button>
      </div>

    </div>
  );
};

export default CartPage;
